import { getNativeCurrency, isNativeCurrencySymbol } from '@/config/native'
import type { TokenCatalogEntry } from '@/data/marketApi/types'
import { NATIVE_TOKEN_ADDRESS, toCatalogAddress } from '@sirius-cybernetics-42/product-config'
import { ChainId, Currency, Token, WETH } from '@/sdk'

export function currencyKey(currency: Currency): string {
  return currency instanceof Token ? currency.address.toLowerCase() : 'native'
}

export function catalogEntryToToken(chainId: ChainId, entry: TokenCatalogEntry): Token {
  const onChain = entry.onChainAddress ?? entry.catalogAddress
  return new Token(
    chainId,
    onChain,
    entry.decimals,
    entry.symbol,
    entry.name,
  )
}

/** Monedas para swap/routing: nativo + ERC-20 visibles + hubs de routing (wrapped). */
export function catalogEntriesToSwapCurrencies(
  chainId: ChainId,
  entries: readonly TokenCatalogEntry[],
): Currency[] {
  const native = getNativeCurrency(chainId)
  const result: Currency[] = [native]
  const seenCatalog = new Set<string>([NATIVE_CATALOG_KEY])

  const nativeEntry = entries.find((entry) => entry.role === 'native')
  if (nativeEntry?.routingHub) {
    const weth = WETH[chainId]
    if (weth) result.push(weth)
  }

  for (const entry of entries) {
    if (entry.role === 'native' || entry.role === 'wrapped_native') continue

    if (entry.pickerPlacement === 'hidden' && !entry.routingHub) continue

    const key = entry.catalogAddress.toLowerCase()
    if (seenCatalog.has(key)) continue
    seenCatalog.add(key)
    result.push(catalogEntryToToken(chainId, entry))
  }

  return result
}

const NATIVE_CATALOG_KEY = NATIVE_TOKEN_ADDRESS.toLowerCase()

/** Monedas visibles en UI: nativo + ERC-20 del catálogo (sin hubs de routing wrapped). */
export function catalogEntriesToVisibleCurrencies(
  chainId: ChainId,
  entries: readonly TokenCatalogEntry[],
): Currency[] {
  const result: Currency[] = []
  for (const entry of getVisibleCatalogEntries(entries)) {
    if (entry.role === 'native') {
      result.push(getNativeCurrency(chainId))
    } else if (entry.role === 'erc20' || entry.role === 'stablecoin') {
      result.push(catalogEntryToToken(chainId, entry))
    }
  }
  return result
}

/** Tokens visibles en picker/explore (sin wrapped). */
export function getVisibleCatalogEntries(entries: readonly TokenCatalogEntry[]): TokenCatalogEntry[] {
  const seen = new Set<string>()
  return entries.filter((entry) => {
    if (entry.pickerPlacement === 'hidden') return false
    if (entry.role === 'wrapped_native') return false
    const key = entry.catalogAddress.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export function getFeaturedCatalogCurrencies(
  chainId: ChainId,
  entries: readonly TokenCatalogEntry[],
): Currency[] {
  const native = getNativeCurrency(chainId)
  const featured = entries.filter((entry) => entry.pickerPlacement === 'featured')
  const erc20Featured = featured
    .filter((entry) => entry.role === 'erc20' || entry.role === 'stablecoin')
    .map((entry) => catalogEntryToToken(chainId, entry))
  return [native, ...erc20Featured]
}

export function getListCatalogCurrencies(
  chainId: ChainId,
  entries: readonly TokenCatalogEntry[],
): Currency[] {
  const featuredKeys = new Set(
    entries
      .filter((entry) => entry.pickerPlacement === 'featured')
      .map((entry) => entry.catalogAddress.toLowerCase()),
  )

  return getVisibleCatalogEntries(entries)
    .filter((entry) => entry.role !== 'native' && entry.pickerPlacement === 'list')
    .filter((entry) => !featuredKeys.has(entry.catalogAddress.toLowerCase()))
    .map((entry) => catalogEntryToToken(chainId, entry))
}

/** Lista scroll «Todas» en picker recibir: catálogo + importados, sin duplicar destacados ni nativo. */
export function buildReceivePickerScrollList(
  selectableTokens: readonly Currency[],
  featuredTokens: readonly Currency[],
): Currency[] {
  const featuredKeys = new Set(featuredTokens.map(currencyKey))
  return selectableTokens.filter((currency) => {
    if (featuredKeys.has(currencyKey(currency))) return false
    if (!(currency instanceof Token)) return false
    return true
  })
}

export function getRoutingHubTokens(
  chainId: ChainId,
  entries: readonly TokenCatalogEntry[],
): Token[] {
  const hubs: Token[] = []
  for (const entry of entries) {
    if (entry.routingHub && entry.role === 'native') {
      const weth = WETH[chainId]
      if (weth) hubs.push(weth)
    }
  }
  return hubs
}

export function isCatalogKnownAddress(
  chainId: ChainId,
  address: string,
  entries: readonly TokenCatalogEntry[],
  userTokens?: readonly Token[],
): boolean {
  const lower = toCatalogAddress(address)
  for (const entry of entries) {
    if (
      toCatalogAddress(entry.catalogAddress) === lower ||
      (entry.onChainAddress != null && toCatalogAddress(entry.onChainAddress) === lower)
    ) {
      return entry.pickerPlacement !== 'hidden' || entry.routingHub === true
    }
  }
  for (const token of userTokens ?? []) {
    if (token.chainId === chainId && toCatalogAddress(token.address) === lower) return true
  }
  return false
}

export function resolveCurrencyFromCatalog(
  chainId: ChainId,
  currencyId: string | undefined,
  entries: readonly TokenCatalogEntry[],
): Currency | undefined {
  if (!currencyId) return undefined
  if (isNativeCurrencySymbol(currencyId, chainId)) {
    return getNativeCurrency(chainId)
  }

  const lower = currencyId.toLowerCase()
  return catalogEntriesToSwapCurrencies(chainId, entries).find(
    (currency): currency is Token =>
      currency instanceof Token && currency.address.toLowerCase() === lower,
  )
}

export function catalogToExploreEntries(
  entries: readonly TokenCatalogEntry[],
): Array<{ address: string; symbol?: string; name?: string }> {
  return getVisibleCatalogEntries(entries).map((entry) => ({
    address: entry.catalogAddress,
    symbol: entry.symbol,
    name: entry.name,
  }))
}
